import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { IconCaretDownFilled } from "@tabler/icons-react";

// Components
import { MButton } from "@/components/ui/atoms/MButtons";
import MHeader from "@/components/ui/organisms/MHeader";
import { MInputField } from "@/components/ui/atoms/MInputField";
import { MChip } from "@/components/ui/atoms/MChip";
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";
import { MDateInputField } from "@/components/ui/molecules/MDateInputField";

// Types
import { PetCareRequest, PetCareType, FrequencyType } from "@/types/petCare";

// Utils
import { addYears, endOfDay, isPast, subDays } from "date-fns";
import {
  DEFAULT_YEARS_AHEAD,
  DEFAULT_TIME_12HR,
  DEFAULT_TIME,
} from "@/utils/constants";
import { getTimezone, updateTime } from "@/utils/date-utils";
import { getMemberInfo } from "@/hooks/useMembers";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import usePetCare from "@/hooks/usePetCare";
import { getMemberName } from "@/utils/member";
import { getReminderOptions, reminderOptions } from "@/utils/reminder-utils";
import MTimeInputField from "@/components/ui/atoms/MTimeInputField";
import CriticalReminderToggle from "@/components/ui/molecules/CriticalReminderToggle";
import { goBackOneLevel } from "@/utils/nav-utils";
import MSpacer from "@/components/ui/atoms/MSpacer";
import styles from "./pet-care.module.css";

const PetCareFormPage: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Get member info and pet care data from location state
  const memberInfo = getMemberInfo();
  const petCare = location.state?.petCare;
  const petCareType = location.state?.petCareType || PetCareType.DEWORMING;
  const isAdding = !petCare;
  const name = getMemberName(memberInfo);
  // Form state
  const [frequencyType, setFrequencyType] = useState<FrequencyType>(
    petCare?.frequencyType || FrequencyType.MONTHLY
  );
  const [frequency, setFrequency] = useState<number | undefined>(
    petCare?.frequency
  );
  const [nextDueDate, setNextDueDate] = useState<Date | undefined>(
    petCare?.nextDueDate ? new Date(petCare.nextDueDate) : undefined
  );
  const [reminderDays, setReminderDays] = useState<number | undefined>(
    petCare?.reminderDays
  );
  const [enabled] = useState<boolean>(petCare?.enabled ?? true);
  const [isCritical, setIsCritical] = useState<boolean>(
    petCare ? petCare.tier === "CRITICAL" : false
  );
  const [isFormValid, setIsFormValid] = useState(false);

  // UI state
  const [reminderDrawerOpen, setReminderDrawerOpen] = useState<boolean>(false);

  // Get pet care hook functions
  const { createPetCare, updatePetCare } = usePetCare(memberInfo.id);

  const [reminderTime, setReminderTime] = useState<string | undefined>(
    petCare?.reminderTime
  );
  const [reminderError, setReminderError] = useState<boolean>(false);

  // Define frequency options
  const frequencyOptionsDeworming = [
    {
      value: FrequencyType.BI_WEEKLY,
      label: t("petcare.label.frequency_bi_weekly"),
    },
    {
      value: FrequencyType.MONTHLY,
      label: t("petcare.label.frequency_monthly"),
    },
    {
      value: FrequencyType.QUATERLY,
      label: t("petcare.label.frequency_quarterly"),
    },
  ];

  const frequencyOptionsFleaControl = [
    {
      value: FrequencyType.MONTHLY,
      label: t("petcare.label.frequency_monthly"),
    },
    {
      value: FrequencyType.QUATERLY,
      label: t("petcare.label.frequency_quarterly"),
    },
    { value: FrequencyType.OTHER, label: t("petcare.label.frequency_other") },
  ];

  const getFrequencyOptions = (petCareType: PetCareType) => {
    switch (petCareType) {
      case PetCareType.DEWORMING:
        return frequencyOptionsDeworming;
      case PetCareType.FLEA_CONTROL:
        return frequencyOptionsFleaControl;
    }
  };

  // Validate form
  const validateForm = (silent: boolean = false) => {
    if (
      !frequencyType ||
      !nextDueDate ||
      reminderDays === null ||
      reminderDays === undefined ||
      !reminderTime
    ) {
      if (!silent) {
        showToast({
          message: t("errors.fill-all-fields"),
          variant: "error",
        });
      }
      return false;
    }

    if (isPast(nextDueDate)) {
      if (!silent) {
        showToast({
          message: t("errors.past-due-date"),
          variant: "error",
        });
      }
      return false;
    }

    if (frequencyType === FrequencyType.OTHER && !frequency) {
      if (!silent) {
        showToast({
          message: t("errors.other-frequency"),
          variant: "error",
        });
      }
      return false;
    }
    return true;
  };

  // Get form data
  const getFormData = (): PetCareRequest => ({
    type: petCareType,
    frequencyType,
    frequency: frequencyType === FrequencyType.OTHER ? frequency : undefined,
    nextDueDate: nextDueDate!,
    reminderDays,
    reminderTime,
    enabled,
    tier: isCritical ? "CRITICAL" : "ROUTINE",
  });

  // Handle saving pet care
  const handleSave = async () => {
    if (validateForm()) {
      try {
        const formData = getFormData();
        formData.timezone = getTimezone();

        if (isAdding) {
          await createPetCare(formData);
        } else {
          await updatePetCare(formData);
        }
        navigate(goBackOneLevel(location.pathname));
      } catch (error) {
        showToast({
          message: t("errors.toast"),
          variant: "error",
        });
      }
    }
  };

  // Handle date selection
  const handleDateSelected = (date: Date) => {
    setNextDueDate(endOfDay(date));
  };

  const getTitle = () => {
    return isAdding
      ? t(`petcare.form.add.${petCareType.toLowerCase()}`, { name })
      : t(`petcare.form.edit.${petCareType.toLowerCase()}`, { name });
  };

  const getDescription = () => {
    return isAdding
      ? t(`petcare.form.add.description`)
      : t(`petcare.form.edit.description`);
  };

  const handleReminderTimeChange = (time: string) => {
    setReminderTime(time);
  };

  const validateReminderDate = (
    dueDate: Date,
    days: number,
    timeStr: string
  ): boolean => {
    if (!dueDate || days === undefined) return true; // No reminder set is valid

    try {
      // Parse the time string (handles both 12h and 24h formats)
      const time = timeStr || DEFAULT_TIME_12HR;
      const reminderDate = updateTime(subDays(dueDate, days), time);
      return !isPast(reminderDate);
    } catch (error) {
      console.error("Error validating reminder date:", error);
      return false;
    }
  };

  // Validate form whenever relevant data changes
  useEffect(() => {
    const isReminderValid =
      reminderDays !== null &&
      reminderDays !== undefined &&
      reminderTime &&
      validateReminderDate(nextDueDate, reminderDays, reminderTime);

    const isValid =
      Boolean(frequencyType) &&
      Boolean(nextDueDate) &&
      reminderDays !== null &&
      reminderDays !== undefined &&
      Boolean(reminderTime) &&
      !(frequencyType === FrequencyType.OTHER && !frequency) &&
      !(nextDueDate && isPast(nextDueDate)) &&
      isReminderValid;

    if (
      reminderDays !== null &&
      reminderDays !== undefined &&
      reminderTime &&
      !isReminderValid
    ) {
      setReminderError(true);
    } else {
      setReminderError(false);
    }
    setIsFormValid(isValid);
  }, [frequencyType, frequency, nextDueDate, reminderDays, reminderTime]);

  const handleReminderDaysSelect = (days: number) => {
    setReminderDays(days);
    // if reminder time is not set then set the same
    if (!reminderTime) {
      setReminderTime(DEFAULT_TIME);
    }
  };

  return (
    <div className={`profile-page-root ${styles.formContainer}`}>
      {/* Form Header */}
      <MHeader
        module="petcare"
        memberInfo={memberInfo}
        title={getTitle()}
        description={getDescription()}
      />
      <MSpacer />
      <div className="profile-input-container">
        {/* Frequency Selection */}
        <div className="field-container">
          <label className="profile-input-label">
            {t(`petcare.label.${petCareType}_frequency_question`)}
          </label>
          <div className={styles.frequencyContainer}>
            {getFrequencyOptions(petCareType).map((option) => (
              <MChip
                key={option.value}
                text={option.label}
                selected={frequencyType === option.value}
                rightIcon={false}
                onClick={() => setFrequencyType(option.value)}
              />
            ))}
          </div>
        </div>

        {frequencyType === FrequencyType.OTHER && (
          <div>
            <label className="profile-input-label">
              {t("petcare.label.frequency_label_other")}
            </label>
            <MInputField
              type="number"
              showLabel={false}
              value={frequency?.toString() || "0"}
              siUnit={t("add_medications.everyXDays.unit")}
              showSIUnit
              onChange={(e) =>
                setFrequency(parseInt(e.target.value) || undefined)
              }
              className="w-[156.5px]"
            />
          </div>
        )}

        {/* Next Due Date */}
        <MDateInputField
          label={t(`petcare.label.next_due_date.${petCareType.toLowerCase()}`)}
          value={nextDueDate}
          onDateChange={handleDateSelected}
          placeholder={t("common.not_answered")}
          minDate={new Date()}
          maxDate={addYears(new Date(), DEFAULT_YEARS_AHEAD)}
          mandatory={true}
        />

        {/* Remind Me */}
        <MInputField
          label={t("petcare.label.reminder")}
          placeholder={t("common.not_answered")}
          value={
            reminderOptions(t, petCareType).find(
              (option) => option.value === reminderDays
            )?.label || ""
          }
          onClick={() => setReminderDrawerOpen(true)}
          icon={<IconCaretDownFilled />}
          readOnly
          showIcon={true}
          disabled={false}
          mandatory={true}
          stateMessage={reminderError ? t("errors.reminder.past") : ""}
          state={reminderError ? "Error" : "Default"}
        />

        {/* Selection Drawer for Reminder Days */}
        <MDropDownDrawer
          open={reminderDrawerOpen}
          onClose={() => setReminderDrawerOpen(false)}
          title={t("petcare.form.reminderDays")}
          items={getReminderOptions(nextDueDate, t, petCareType).map(
            (option) => ({
              value: option.value.toString(),
              label: option.label,
            })
          )}
          onSelect={(value) => handleReminderDaysSelect(Number(value))}
        />

        {/* Time Picker */}
        <MTimeInputField
          label={t("petcare.form.reminderTime")}
          value={reminderTime}
          onChange={handleReminderTimeChange}
          showLabel={true}
          variant="Underlined"
          size="md"
          mandatory={true}
        />

        <CriticalReminderToggle checked={isCritical} onChange={setIsCritical} />
      </div>

      {/* Save Button */}
      <div className="confirm-footer">
        <MButton
          onClick={handleSave}
          state={isFormValid ? "default" : "disabled"}
          variant="filled"
          size="lg"
          className="w-full"
          text={isAdding ? t("common.confirm") : t("common.done")}
          disabled={!isFormValid}
        />
      </div>
    </div>
  );
};

export default PetCareFormPage;
